/**
 * Counting the shelf.
 *
 * Optimised for one situation: a person holding a phone in one hand and a
 * product in the other, working down a shelf, wanting to be finished. Every
 * decision below serves that:
 *
 *  - **the input is empty and numeric.** Never prefilled with the expected
 *    figure — seeding it turns counting into confirming, and a tired person at
 *    closing time confirms. `inputMode="numeric"` brings up the number pad.
 *  - **one row, one tap, one number.** No opening a drawer per product, no
 *    confirmation per line. The count saves as soon as the field is left.
 *  - **the difference appears the instant it is known**, in colour, so a
 *    mistyped 8 for 80 is visible immediately rather than at approval.
 *  - **expected is shown, but quietly.** Hiding it would make honest
 *    recounting impossible; shouting it would anchor the counter to it.
 *
 * Approving is a separate, deliberate act with its own confirmation, because
 * it rewrites the ledger under the approver's name.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Camera,
  Check,
  ClipboardList,
  Play,
  ScanLine,
  X,
} from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { ApiError, api } from '../../lib/api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Modal,
  Spinner,
} from '../../components/ui';
import { useRecordLocation } from '../../lib/locationContext';
import { money, moment, quantity } from './format';

interface CountLine {
  id: string;
  productId: string;
  productName: string;
  productBarcode: string | null;
  expectedQuantity: number;
  countedQuantity: number | null;
  difference: number | null;
  unitCost: string | null;
  varianceValue: string | null;
  note: string | null;
  countedBy: string | null;
  countedAt: string | null;
  appliedDelta: number | null;
}

interface CountView {
  id: string;
  branchId: string | null;
  branchName: string | null;
  status: string;
  startedAt: string;
  startedBy: string | null;
  appliedAt: string | null;
  approvedBy: string | null;
  note: string | null;
  lines: CountLine[];
  totals: {
    products: number;
    counted: number;
    shortages: number;
    overages: number;
    exact: number;
    netVarianceValue: string;
    shortageValue: string;
  };
}

/** What the server says a scan found, and what it did about it. */
interface ScanResult {
  line: CountLine;
  alreadyCounted: boolean;
  action: 'located' | 'set' | 'incremented';
}

/** Which lines the counter wants in front of them right now. */
type LineFilter = 'all' | 'uncounted' | 'differences';

interface CountSummary {
  id: string;
  branchName: string | null;
  status: string;
  startedAt: string;
  startedBy: string | null;
  appliedAt: string | null;
  approvedBy: string | null;
  productsCounted: number;
  shortages: number;
  overages: number;
  netVarianceValue: string;
}

/** The list: past counts, and the way in to a new one. */
export function StockCountsPage(): ReactNode {
  const { business, can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currency = business?.currencyCode ?? 'USD';
  const timezone = business?.timezone ?? 'UTC';
  const path = (suffix: string) => `/businesses/${business?.businessId}${suffix}`;

  /**
   * A count is somebody standing in one place counting what is on the shelf
   * there, so it names that place. "Whole business" is no longer on offer: a
   * variance spanning three shops tells nobody which shelf is short.
   */
  const location = useRecordLocation();
  const [error, setError] = useState<string | null>(null);

  const counts = useQuery({
    queryKey: ['stock-counts', business?.businessId],
    queryFn: () => api.get<{ data: CountSummary[] }>(path('/stock-counts')),
    enabled: Boolean(business),
  });

  const start = useMutation({
    mutationFn: () =>
      api.post<CountView>(path('/stock-counts'), { branchId: location.branchId }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['stock-counts'] });
      navigate(`/stock-counts/${created.id}`);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not start a count.'),
  });

  const open = counts.data?.data.find((c) => c.status === 'open');

  return (
    <div className="space-y-4 pb-10">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Stock counts</h1>
        <p className="text-sm text-slate-500">
          Count what is physically on the shelf and compare it with the books.
        </p>
      </header>

      {error && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {error}
        </div>
      )}

      {can('stock:count') && (
        <Card>
          <CardHeader
            title={open ? 'A count is already open' : 'Start a new count'}
            subtitle={
              open
                ? 'Finish or cancel it before starting another for the same place.'
                : 'Pick the location you are standing in.'
            }
          />
          <div className="flex flex-wrap items-end gap-2 p-4 pt-0">
            {location.locations.length > 1 && (
              <label className="text-xs text-slate-500">
                Location
                <select
                  value={location.branchId}
                  onChange={(e) => location.setBranchId(e.target.value)}
                  aria-label="Location"
                  className="mt-0.5 block rounded-lg border border-slate-200 px-2 py-2 text-sm"
                >
                  <option value="">Choose a location</option>
                  {location.locations.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {open ? (
              <Button onClick={() => navigate(`/stock-counts/${open.id}`)}>
                <ClipboardList className="h-4 w-4" />
                Continue counting
              </Button>
            ) : (
              <Button
                onClick={() => start.mutate()}
                disabled={start.isPending || location.unresolved}
                {...(location.unresolved ? { title: 'Choose the location you are counting' } : {})}
              >
                <Play className="h-4 w-4" />
                {start.isPending ? 'Starting…' : 'Start count'}
              </Button>
            )}
          </div>
        </Card>
      )}

      <Card>
        <CardHeader title="Previous counts" subtitle="What was found, and who approved it." />
        <div className="p-4 pt-0">
          {counts.isLoading && <Spinner label="Loading counts" />}
          {counts.isError && <ErrorState message="Could not load previous counts." />}
          {counts.data?.data.length === 0 && (
            <EmptyState
              title="No counts yet"
              description="A count is the only way to prove what is actually on the shelf."
            />
          )}

          <ul className="space-y-2">
            {counts.data?.data.map((count) => (
              <li key={count.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/stock-counts/${count.id}`)}
                  className="w-full rounded-xl border border-slate-200 p-3 text-left"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-medium text-slate-900">
                      {count.branchName ?? 'Whole business'}
                    </span>
                    <Badge
                      tone={
                        count.status === 'applied'
                          ? 'success'
                          : count.status === 'open'
                            ? 'info'
                            : 'neutral'
                      }
                    >
                      {count.status}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {moment(count.startedAt, timezone)}
                    {count.startedBy && <> · started by {count.startedBy}</>}
                    {count.approvedBy && <> · approved by {count.approvedBy}</>}
                  </p>
                  <p className="mt-1 text-xs">
                    <span className="text-slate-600">{count.productsCounted} counted</span>
                    {count.shortages > 0 && (
                      <span className="ml-2 text-rose-700">{count.shortages} short</span>
                    )}
                    {count.overages > 0 && (
                      <span className="ml-2 text-amber-700">{count.overages} over</span>
                    )}
                    {Number(count.netVarianceValue) !== 0 && (
                      <span
                        className={`ml-2 tabular-nums ${
                          Number(count.netVarianceValue) < 0 ? 'text-rose-700' : 'text-amber-700'
                        }`}
                      >
                        {money(count.netVarianceValue, currency)}
                      </span>
                    )}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </Card>
    </div>
  );
}

/** The counting screen itself. */
export function StockCountSessionPage(): ReactNode {
  const { id = '' } = useParams();
  const { business, can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currency = business?.currencyCode ?? 'USD';
  const timezone = business?.timezone ?? 'UTC';
  const path = (suffix: string) => `/businesses/${business?.businessId}${suffix}`;

  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [filter, setFilter] = useState<LineFilter>('all');
  const [lastScan, setLastScan] = useState<ScanResult | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);

  const count = useQuery({
    queryKey: ['stock-count', business?.businessId, id],
    queryFn: () => api.get<CountView>(path(`/stock-counts/${id}`)),
    enabled: Boolean(business && id),
  });

  const saveLine = useMutation({
    mutationFn: (input: { lineId: string; countedQuantity: number | null; note?: string | null }) =>
      api.patch<CountLine>(path(`/stock-counts/${id}/lines/${input.lineId}`), {
        countedQuantity: input.countedQuantity,
        note: input.note ?? null,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['stock-count'] }),
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save that count.'),
  });

  /**
   * A scan.
   *
   * The server resolves the barcode inside this tenant and tells us what it
   * found and what it did; the screen never guesses which product a code
   * belongs to, because barcodes are unique only within a business and a
   * client-side match would be a match against the wrong shop.
   */
  const scan = useMutation({
    mutationFn: (input: { barcode: string; countedQuantity?: number; increment?: number }) =>
      api.post<ScanResult>(path(`/stock-counts/${id}/scan`), input),
    onSuccess: (result) => {
      setLastScan(result);
      setScanError(null);
      void queryClient.invalidateQueries({ queryKey: ['stock-count'] });
    },
    onError: (caught) => {
      // The last good scan is cleared: leaving it up under an error message
      // invites somebody to read the previous product as this one's answer.
      setLastScan(null);
      setScanError(
        caught instanceof ApiError ? caught.message : 'Could not read that barcode.',
      );
    },
  });

  const apply = useMutation({
    mutationFn: () => api.post<CountView>(path(`/stock-counts/${id}/apply`), {}),
    onSuccess: () => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ['stock-count'] });
      void queryClient.invalidateQueries({ queryKey: ['accountability'] });
    },
    onError: (caught) => {
      setConfirming(false);
      setError(caught instanceof ApiError ? caught.message : 'Could not approve this count.');
    },
  });

  const cancel = useMutation({
    mutationFn: () => api.post(path(`/stock-counts/${id}/cancel`), {}),
    onSuccess: () => navigate('/stock-counts'),
  });

  if (count.isLoading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner label="Loading the count" />
      </div>
    );
  }

  if (count.isError || !count.data) {
    return <ErrorState message="Could not load this stock count." onRetry={() => void count.refetch()} />;
  }

  const data = count.data;
  const isOpen = data.status === 'open';
  const remaining = data.totals.products - data.totals.counted;
  const canCount = isOpen && can('stock:count');

  const uncounted = data.lines.filter((l) => l.countedQuantity === null);
  const differences = data.lines.filter((l) => l.difference !== null && l.difference !== 0);
  const visible =
    filter === 'uncounted' ? uncounted : filter === 'differences' ? differences : data.lines;
  const scannable = data.lines.some((l) => l.productBarcode);

  return (
    <div className="space-y-4 pb-24">
      <button
        type="button"
        onClick={() => navigate('/stock-counts')}
        className="inline-flex items-center gap-1 text-sm text-slate-600"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All counts
      </button>

      <header>
        <h1 className="text-xl font-semibold text-slate-900">
          {data.branchName ?? 'Whole business'}
        </h1>
        <p className="text-sm text-slate-500">
          Started {moment(data.startedAt, timezone)}
          {data.startedBy && <> by {data.startedBy}</>}
          {data.approvedBy && <> · approved by {data.approvedBy}</>}
        </p>
      </header>

      {error && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {error}
        </div>
      )}

      {!isOpen && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          This count is {data.status} and can no longer be changed.
        </div>
      )}

      {/* Progress and money at stake, pinned above the list. */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Counted" value={`${data.totals.counted}/${data.totals.products}`} />
        <Stat label="Short" value={String(data.totals.shortages)} tone={data.totals.shortages > 0 ? 'bad' : 'good'} />
        <Stat label="Over" value={String(data.totals.overages)} tone={data.totals.overages > 0 ? 'warn' : 'good'} />
        <Stat
          label="Value at stake"
          value={money(data.totals.netVarianceValue, currency)}
          tone={Number(data.totals.netVarianceValue) < 0 ? 'bad' : 'good'}
        />
      </div>

      {canCount && (
        <ScanPanel
          currency={currency}
          scanning={scan.isPending}
          result={lastScan}
          error={scanError}
          anyBarcodes={scannable}
          onScan={(input) => scan.mutate(input)}
          onDismiss={() => {
            setLastScan(null);
            setScanError(null);
          }}
        />
      )}

      {data.lines.length > 0 && (
        <FilterTabs
          filter={filter}
          onChange={setFilter}
          counts={{
            all: data.lines.length,
            uncounted: uncounted.length,
            differences: differences.length,
          }}
        />
      )}

      {data.lines.length === 0 ? (
        <EmptyState
          title="Nothing to count"
          description="No stock-tracked products at this location yet."
        />
      ) : visible.length === 0 ? (
        <EmptyState
          title={filter === 'uncounted' ? 'Everything has been counted' : 'No differences'}
          description={
            filter === 'uncounted'
              ? 'Every product on this count now has a figure against it.'
              : 'Every product you counted matches what the books say.'
          }
        />
      ) : (
        <ul className="space-y-2">
          {visible.map((line) => (
            <CountRow
              key={line.id}
              line={line}
              currency={currency}
              editable={canCount}
              saving={saveLine.isPending}
              /* The row the last scan landed on, so the eye finds it without
                 hunting down a list of two hundred products. */
              highlighted={lastScan?.line.id === line.id}
              onSave={(value, note) =>
                saveLine.mutate({ lineId: line.id, countedQuantity: value, note })
              }
              noteOpen={noteFor === line.id}
              onToggleNote={() => setNoteFor(noteFor === line.id ? null : line.id)}
            />
          ))}
        </ul>
      )}

      {isOpen && (
        <div className="fixed inset-x-0 bottom-0 border-t border-slate-200 bg-white/95 p-3 backdrop-blur sm:static sm:border-0 sm:bg-transparent sm:p-0">
          <div className="mx-auto flex max-w-3xl items-center gap-2">
            {can('stock:manage') ? (
              <Button
                onClick={() => setConfirming(true)}
                disabled={data.totals.counted === 0}
                className="flex-1"
              >
                <Check className="h-4 w-4" />
                Approve and adjust stock
              </Button>
            ) : (
              /* Staff may count; only a manager may change the books. */
              <p className="flex-1 text-xs text-slate-500">
                {data.totals.counted} counted. Ask an owner or manager to approve the adjustment.
              </p>
            )}
            <Button variant="ghost" onClick={() => cancel.mutate()}>
              <X className="h-4 w-4" />
              Cancel
            </Button>
          </div>
        </div>
      )}

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Approve this count?"
      >
        {/*
          The variance summary, in full, before anything changes.

          The old version of this modal gave totals only. Totals are the right
          headline and the wrong basis for a decision: an owner approving a
          write-off is entitled to see which products it is made of, at the
          moment of approving, without going back to hunt through the list.
        */}
        <div className="space-y-3 text-sm text-slate-700">
          <p>
            This will adjust stock for {data.totals.counted} product
            {data.totals.counted === 1 ? '' : 's'} and record you as the approver.
          </p>

          {differences.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-left text-xs">
                <caption className="sr-only">Differences found by this count</caption>
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th scope="col" className="px-2 py-1.5 font-medium">Product</th>
                    <th scope="col" className="px-2 py-1.5 text-right font-medium">Books</th>
                    <th scope="col" className="px-2 py-1.5 text-right font-medium">Counted</th>
                    <th scope="col" className="px-2 py-1.5 text-right font-medium">Difference</th>
                    <th scope="col" className="px-2 py-1.5 text-right font-medium">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {differences.map((line) => (
                    <tr key={line.id} className="border-t border-slate-100">
                      <td className="px-2 py-1.5">{line.productName}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {quantity(line.expectedQuantity)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {quantity(line.countedQuantity ?? 0)}
                      </td>
                      <td
                        className={`px-2 py-1.5 text-right tabular-nums ${
                          (line.difference ?? 0) < 0 ? 'text-rose-700' : 'text-amber-700'
                        }`}
                      >
                        {(line.difference ?? 0) > 0 ? '+' : ''}
                        {quantity(line.difference ?? 0)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {line.varianceValue ? money(line.varianceValue, currency) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-emerald-900">
              Everything you counted matches the books. Approving changes no stock.
            </p>
          )}

          {remaining > 0 && (
            <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {remaining} product{remaining === 1 ? '' : 's'} were not counted and will be left
              exactly as they are.
            </p>
          )}

          {Number(data.totals.shortageValue) > 0 && (
            <p className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-rose-900">
              Missing stock worth {money(data.totals.shortageValue, currency)} will be written off.
            </p>
          )}

          <p className="text-xs text-slate-500">
            The count itself is kept forever, exactly as recorded, whatever happens next.
          </p>

          <div className="flex gap-2 pt-1">
            <Button onClick={() => apply.mutate()} disabled={apply.isPending}>
              {apply.isPending ? 'Approving…' : 'Yes, approve'}
            </Button>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Not yet
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/**
 * The scanner.
 *
 * Built for the two ways a scan actually reaches a screen in a Nigerian shop:
 * a USB or Bluetooth gun that types the digits and presses Enter, and a phone
 * camera. The input is the common path for both, which is why the camera
 * writes into it rather than bypassing it — one code path, one set of errors.
 *
 * Three decisions worth stating:
 *
 *  - **+1 per scan is the default.** Walking a shelf, you beep each carton as
 *    you move it. Typing a figure is the exception, offered next to it.
 *  - **The field clears and refocuses after every scan.** A scanner that has
 *    to be clicked back into is a scanner nobody uses past the third product.
 *  - **A repeat scan is flagged, never blocked.** Passing the same carton
 *    twice is the commonest error in scanned counting, and scanning the second
 *    carton is how you count it. Only the person holding it knows which.
 */
function ScanPanel({
  currency,
  scanning,
  result,
  error,
  anyBarcodes,
  onScan,
  onDismiss,
}: {
  currency: string;
  scanning: boolean;
  result: ScanResult | null;
  error: string | null;
  anyBarcodes: boolean;
  onScan: (input: { barcode: string; countedQuantity?: number; increment?: number }) => void;
  onDismiss: () => void;
}): ReactNode {
  const [barcode, setBarcode] = useState('');
  const [mode, setMode] = useState<'increment' | 'quantity'>('increment');
  const [amount, setAmount] = useState('');
  const [camera, setCamera] = useState(false);
  const field = useRef<HTMLInputElement>(null);

  // Ready for the next carton the instant the last one resolves.
  useEffect(() => {
    if (!scanning) field.current?.focus();
  }, [scanning, result, error]);

  const submit = (): void => {
    const code = barcode.trim();
    if (code === '') return;

    if (mode === 'quantity') {
      const typed = amount.trim();
      // No figure yet: locate the product and let them type into the row that
      // opens, rather than guessing a number on their behalf.
      if (typed === '') {
        onScan({ barcode: code });
      } else {
        const value = Number(typed);
        if (Number.isNaN(value)) return;
        onScan({ barcode: code, countedQuantity: value });
      }
      setAmount('');
    } else {
      onScan({ barcode: code, increment: 1 });
    }

    setBarcode('');
  };

  return (
    <Card>
      <CardHeader
        title="Scan to count"
        subtitle={
          anyBarcodes
            ? 'Point a scanner at the carton, or type the code.'
            : 'No product here has a barcode yet. Add one to a product to scan it.'
        }
      />

      <div className="space-y-3 p-4 pt-0">
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-0 flex-1">
            <label className="text-xs text-slate-500" htmlFor="barcode">
              Barcode
            </label>
            <div className="mt-0.5 flex gap-2">
              <input
                id="barcode"
                ref={field}
                type="text"
                value={barcode}
                autoComplete="off"
                /* A scanner emits digits fast and finishes with Enter. */
                onChange={(e) => setBarcode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submit();
                  }
                }}
                placeholder="Scan or type"
                disabled={scanning}
                className="w-full rounded-lg border border-slate-300 px-2 py-2 text-base tabular-nums disabled:bg-slate-100"
              />
              <CameraScanButton
                open={camera}
                onOpen={() => setCamera(true)}
                onClose={() => setCamera(false)}
                onDetected={(code) => {
                  setCamera(false);
                  setBarcode(code);
                  if (mode === 'increment') onScan({ barcode: code, increment: 1 });
                }}
              />
            </div>
          </div>

          {mode === 'quantity' && (
            <div>
              <label className="text-xs text-slate-500" htmlFor="scan-quantity">
                Counted
              </label>
              <input
                id="scan-quantity"
                inputMode="numeric"
                type="text"
                value={amount}
                disabled={scanning}
                placeholder="—"
                onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submit();
                  }
                }}
                className="mt-0.5 w-24 rounded-lg border border-slate-300 px-2 py-2 text-right text-base tabular-nums disabled:bg-slate-100"
              />
            </div>
          )}

          <Button onClick={submit} disabled={scanning || barcode.trim() === ''}>
            <ScanLine className="h-4 w-4" />
            {scanning ? 'Finding…' : mode === 'increment' ? 'Add one' : 'Record'}
          </Button>
        </div>

        <fieldset className="flex flex-wrap items-center gap-3 text-xs">
          <legend className="sr-only">How each scan is recorded</legend>
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              name="scan-mode"
              checked={mode === 'increment'}
              onChange={() => setMode('increment')}
            />
            Add one per scan
          </label>
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              name="scan-mode"
              checked={mode === 'quantity'}
              onChange={() => setMode('quantity')}
            />
            Type the quantity
          </label>
        </fieldset>

        {error && (
          <p
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-900"
          >
            {error}
          </p>
        )}

        {result && <ScanResultCard result={result} currency={currency} onDismiss={onDismiss} />}
      </div>
    </Card>
  );
}

/** What the last scan found, large enough to read without stopping. */
function ScanResultCard({
  result,
  currency,
  onDismiss,
}: {
  result: ScanResult;
  currency: string;
  onDismiss: () => void;
}): ReactNode {
  const { line } = result;
  const difference = line.difference;
  const tone =
    difference === null
      ? 'border-slate-200 bg-slate-50'
      : difference < 0
        ? 'border-rose-300 bg-rose-50'
        : difference > 0
          ? 'border-amber-300 bg-amber-50'
          : 'border-emerald-200 bg-emerald-50';

  return (
    <div role="status" className={`rounded-xl border p-3 ${tone}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-slate-900">{line.productName}</p>
          {line.productBarcode && (
            <p className="text-xs tabular-nums text-slate-500">{line.productBarcode}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss scan result"
          className="shrink-0 text-slate-400"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <dl className="mt-2 grid grid-cols-3 gap-2 text-sm">
        <div>
          <dt className="text-xs text-slate-500">Books say</dt>
          <dd className="tabular-nums">{quantity(line.expectedQuantity)}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Counted</dt>
          <dd className="font-semibold tabular-nums">
            {line.countedQuantity === null ? 'Not yet' : quantity(line.countedQuantity)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Difference</dt>
          <dd className="tabular-nums">
            {difference === null ? (
              '—'
            ) : (
              <span className={difference < 0 ? 'text-rose-700' : difference > 0 ? 'text-amber-700' : 'text-emerald-700'}>
                {difference > 0 ? '+' : ''}
                {quantity(difference)}
                {line.varianceValue && Number(line.varianceValue) !== 0 && (
                  <> · {money(line.varianceValue, currency)}</>
                )}
              </span>
            )}
          </dd>
        </div>
      </dl>

      {result.alreadyCounted && result.action === 'incremented' && (
        /* Flagged, not blocked — see the note on ScanPanel. */
        <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          Already counted before this scan. If you scanned the same one twice, scan it again
          in reverse or type the right figure on its row.
        </p>
      )}

      {line.countedBy && (
        <p className="mt-1 text-xs text-slate-500">Counted by {line.countedBy}</p>
      )}
    </div>
  );
}

/**
 * Camera scanning, when the browser can do it.
 *
 * `BarcodeDetector` is native on Chrome and Android WebView, which is what the
 * overwhelming majority of shop phones run. Where it is absent the button
 * simply does not appear: a camera button that opens a camera that cannot read
 * anything is worse than no button, because it costs the person their place on
 * the shelf to find out.
 */
function CameraScanButton({
  open,
  onOpen,
  onClose,
  onDetected,
}: {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onDetected: (barcode: string) => void;
}): ReactNode {
  const video = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const supported = useMemo(
    () => typeof window !== 'undefined' && 'BarcodeDetector' in window,
    [],
  );

  useEffect(() => {
    if (!open || !supported) return undefined;

    let stream: MediaStream | null = null;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const run = async (): Promise<void> => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (stopped) return;
        if (video.current) {
          video.current.srcObject = stream;
          await video.current.play();
        }

        const Detector = (window as unknown as { BarcodeDetector: new () => {
          detect: (source: CanvasImageSource) => Promise<Array<{ rawValue: string }>>;
        } }).BarcodeDetector;
        const detector = new Detector();

        const poll = async (): Promise<void> => {
          if (stopped || !video.current) return;
          try {
            const found = await detector.detect(video.current);
            const code = found[0]?.rawValue?.trim();
            if (code) {
              onDetected(code);
              return;
            }
          } catch {
            // A frame that cannot be decoded is the normal case, not an error.
          }
          timer = setTimeout(() => void poll(), 250);
        };

        void poll();
      } catch {
        setFailed('Could not open the camera. Type the code instead.');
      }
    };

    void run();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [open, supported, onDetected]);

  if (!supported) return null;

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        aria-label="Scan with the camera"
        className="shrink-0 rounded-lg border border-slate-300 px-3"
      >
        <Camera className="h-4 w-4" aria-hidden />
      </button>

      <Modal open={open} onClose={onClose} title="Scan with the camera">
        <div className="space-y-2">
          {failed ? (
            <p className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
              {failed}
            </p>
          ) : (
            <video
              ref={video}
              muted
              playsInline
              className="w-full rounded-lg bg-slate-900"
              aria-label="Camera preview"
            />
          )}
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </Modal>
    </>
  );
}

/**
 * All / Uncounted / Differences.
 *
 * "Uncounted" is the one that matters: two hundred products in, the only
 * question left is which shelves have not been walked, and scrolling a full
 * list to answer it is how the last twenty get skipped.
 */
function FilterTabs({
  filter,
  onChange,
  counts,
}: {
  filter: LineFilter;
  onChange: (next: LineFilter) => void;
  counts: { all: number; uncounted: number; differences: number };
}): ReactNode {
  const tabs: Array<{ key: LineFilter; label: string; count: number }> = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'uncounted', label: 'Uncounted', count: counts.uncounted },
    { key: 'differences', label: 'Differences', count: counts.differences },
  ];

  return (
    <div role="tablist" aria-label="Which products to show" className="flex gap-2 overflow-x-auto">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={filter === tab.key}
          onClick={() => onChange(tab.key)}
          className={`shrink-0 rounded-full border px-3 py-1.5 text-sm ${
            filter === tab.key
              ? 'border-indigo-600 bg-indigo-600 text-white'
              : 'border-slate-200 bg-white text-slate-700'
          }`}
        >
          {tab.label}
          <span className="ml-1.5 tabular-nums opacity-80">{tab.count}</span>
        </button>
      ))}
    </div>
  );
}

function Stat({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  tone?: 'good' | 'warn' | 'bad' | 'neutral';
}): ReactNode {
  const classes = {
    good: 'border-slate-200 bg-white',
    warn: 'border-amber-200 bg-amber-50 text-amber-900',
    bad: 'border-rose-200 bg-rose-50 text-rose-900',
    neutral: 'border-slate-200 bg-white',
  }[tone];

  return (
    <div className={`rounded-xl border p-2 ${classes}`}>
      <p className="text-xs opacity-70">{label}</p>
      <p className="text-base font-semibold tabular-nums">{value}</p>
    </div>
  );
}

/**
 * One product, one number.
 *
 * Local state while typing so the field never fights the cursor, saved when
 * the field is left. A count that needed a Save button per row would be
 * abandoned halfway down the shelf.
 */
function CountRow({
  line,
  currency,
  editable,
  saving,
  highlighted = false,
  onSave,
  noteOpen,
  onToggleNote,
}: {
  line: CountLine;
  currency: string;
  editable: boolean;
  saving: boolean;
  /** The row the last scan landed on. */
  highlighted?: boolean;
  onSave: (value: number | null, note: string | null) => void;
  noteOpen: boolean;
  onToggleNote: () => void;
}): ReactNode {
  const [draft, setDraft] = useState(
    line.countedQuantity === null ? '' : String(line.countedQuantity),
  );
  const [note, setNote] = useState(line.note ?? '');

  /**
   * A scan can change this row's figure while the row is on screen.
   *
   * The draft is local so typing never fights the cursor, which means it has
   * to be told when the server figure moved underneath it — otherwise the row
   * keeps displaying the number from before the scan, and the next blur writes
   * that stale number back over the scan.
   */
  const [synced, setSynced] = useState(line.countedQuantity);
  if (synced !== line.countedQuantity) {
    setSynced(line.countedQuantity);
    setDraft(line.countedQuantity === null ? '' : String(line.countedQuantity));
  }

  const difference = line.difference;
  const tone =
    difference === null
      ? 'border-slate-200'
      : difference < 0
        ? 'border-rose-300 bg-rose-50'
        : difference > 0
          ? 'border-amber-300 bg-amber-50'
          : 'border-emerald-200 bg-emerald-50';

  const commit = (): void => {
    const trimmed = draft.trim();
    const next = trimmed === '' ? null : Number(trimmed);
    if (next !== null && Number.isNaN(next)) return;
    if (next === line.countedQuantity) return;
    onSave(next, note.trim() === '' ? null : note.trim());
  };

  return (
    <li className={`rounded-xl border p-3 ${tone} ${highlighted ? 'ring-2 ring-indigo-500' : ''}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-slate-900">{line.productName}</p>
          {/* Expected is present but quiet: hiding it makes honest recounting
              impossible, shouting it anchors the counter to it. */}
          <p className="text-xs text-slate-500">
            Books say {quantity(line.expectedQuantity)}
            {line.unitCost && <> · {money(line.unitCost, currency)} each</>}
          </p>
          {line.productBarcode && (
            <p className="text-xs tabular-nums text-slate-400">{line.productBarcode}</p>
          )}
        </div>

        <div className="shrink-0 text-right">
          <label className="sr-only" htmlFor={`count-${line.id}`}>
            Counted quantity for {line.productName}
          </label>
          <input
            id={`count-${line.id}`}
            // Numeric keypad on a phone; `text` rather than `number` so a
            // stray scroll cannot silently change a recorded count.
            inputMode="numeric"
            type="text"
            value={draft}
            disabled={!editable || saving}
            placeholder="—"
            onChange={(e) => setDraft(e.target.value.replace(/[^\d.]/g, ''))}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            className="w-20 rounded-lg border border-slate-300 px-2 py-2 text-right text-lg tabular-nums disabled:bg-slate-100"
          />
        </div>
      </div>

      {difference !== null && (
        <p className="mt-2 text-sm font-medium tabular-nums">
          {difference === 0 ? (
            <span className="text-emerald-700">Matches the books</span>
          ) : (
            <span className={difference < 0 ? 'text-rose-700' : 'text-amber-700'}>
              {difference > 0 ? '+' : ''}
              {quantity(difference)}
              {line.varianceValue && <> · {money(line.varianceValue, currency)}</>}
            </span>
          )}
          {line.countedBy && (
            <span className="ml-2 text-xs font-normal text-slate-500">by {line.countedBy}</span>
          )}
        </p>
      )}

      {editable && (
        <>
          <button
            type="button"
            onClick={onToggleNote}
            className="mt-1 text-xs font-medium text-indigo-700"
          >
            {noteOpen ? 'Hide note' : line.note ? 'Edit note' : 'Add a note'}
          </button>
          {noteOpen && (
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={commit}
              placeholder="Why is it different?"
              className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
            />
          )}
        </>
      )}

      {!editable && line.note && (
        <p className="mt-1 text-xs italic text-slate-600">{line.note}</p>
      )}
    </li>
  );
}
